import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators';
import * as ScreeningCriteriaActions from './screening-criteria.actions';
import {ScreeningCriteriaService} from './screening-criteria.service';
import {ScreeningCriteriaDTO} from '../../dto/screening-criteria';

@Injectable()
export class ScreeningCriteriaEffects {

  private readonly criteriaService: ScreeningCriteriaService = inject(ScreeningCriteriaService);
  private readonly actions$: Actions = inject(Actions);

  loadScreeningCriterias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningCriteriaActions.loadScreeningCriterias),
      switchMap((action) =>
        this.criteriaService.getCriteria(action.projectId).pipe(
          map((criteria: ScreeningCriteriaDTO[]) =>
            ScreeningCriteriaActions.loadScreeningCriteriasSuccess({projectId: action.projectId, criteria})
          ),
          catchError((error) =>
            of(ScreeningCriteriaActions.loadScreeningCriteriasFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  createScreeningCriteria$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningCriteriaActions.createScreeningCriteria),
      mergeMap((action) =>
        this.criteriaService.createCriteria(action.projectId, action.criteria).pipe(
          map((criteria: ScreeningCriteriaDTO) =>
            ScreeningCriteriaActions.createScreeningCriteriaSuccess({projectId: action.projectId, criteria})
          ),
          catchError((error) =>
            of(ScreeningCriteriaActions.createScreeningCriteriaFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  deleteScreeningCriteria$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningCriteriaActions.deleteScreeningCriteria),
      mergeMap((action) =>
        this.criteriaService.deleteCriteria(action.projectId, action.id).pipe(
          map(() =>
            ScreeningCriteriaActions.deleteScreeningCriteriaSuccess({projectId: action.projectId, id: action.id})
          ),
          catchError((error) =>
            of(ScreeningCriteriaActions.deleteScreeningCriteriaFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  retrieveScreeningCriteria$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningCriteriaActions.retrieveScreeningCriteria),
      switchMap((action) =>
        this.criteriaService.getCriteriaById(action.projectId, action.id).pipe(
          map((criteria: ScreeningCriteriaDTO) =>
            ScreeningCriteriaActions.retrieveScreeningCriteriaSuccess({projectId: action.projectId, criteria})
          ),
          catchError((error) =>
            of(ScreeningCriteriaActions.retrieveScreeningCriteriaFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  updateScreeningCriteria$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreeningCriteriaActions.updateScreeningCriteria),
      mergeMap((action) =>
        this.criteriaService.updateCriteria(action.projectId, action.id, action.criteria).pipe(
          map((criteria: ScreeningCriteriaDTO) =>
            ScreeningCriteriaActions.updateScreeningCriteriaSuccess({projectId: action.projectId, criteria})
          ),
          catchError((error) =>
            of(ScreeningCriteriaActions.updateScreeningCriteriaFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );
}
